import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiEye, FiDownload, FiShare2, FiEdit, FiTrash } from "react-icons/fi";

// Shipment and payment modes
type ShipmentStatus = "pending" | "in_transit" | "delivered" | "draft";
type PaymentMode = "paid" | "to_pay" | "credit";
type FilterOption = "all" | ShipmentStatus;

const API_BASE = process.env.REACT_APP_API_URL;

// APIConsignment interface according to the API response sample
interface APIConsignment {
  id: number;
  sender: number;
  company: number;
  receiver: number;
  transporter: number;
  driver: number;
  vehicles: number;
  payment: string;
  source: string;
  destination: string;
  receiverAmountStatatus: string; // intentional typo, matches API
  senderAmountStatatus: string;
  transporterAmountStatatus: string;
  paymentStatus: PaymentMode;
  paymentPayableConsignor: number;
  ItemsDescription: string;
  ["InvoiceNo"]?: string;
  ["InvoiceNo."]?: string;
  EWAYBILLNO: string;
  subjects: string;
  link?: string;
  status?: ShipmentStatus; // Not present in sample, but kept for filtering fallback
}

// Helper to get Invoice Number
const getInvoiceNo = (consignment: APIConsignment): string =>
  consignment.InvoiceNo ||
  //@ts-ignore
  consignment["InvoiceNo."] ||
  "";

// Status logic: fallback to paymentStatus to map our stat values for all API rows
const getConsignmentStatus = (consignment: APIConsignment): ShipmentStatus => {
  // Explicit status preferred, fallback as per business logic
  if (consignment.status) return consignment.status;
  // No "in_transit" or "draft" state in API, so infer from paymentStatus
  if (consignment.paymentStatus === "to_pay" || consignment.paymentStatus === "credit") return "pending";
  if (consignment.paymentStatus === "paid") return "delivered";
  return "pending";
};

const FILTER_OPTIONS: { key: FilterOption; label: string }[] = [
  { key: "all",       label: "All"        },
  { key: "pending",   label: "Pending"    },
  { key: "in_transit",label: "In transit" },
  { key: "delivered", label: "Delivered"  },
  { key: "draft",     label: "Draft"      },
];

// Color classes for status badges
const STATUS_STYLES: Record<string, string> = {
  pending:    "bg-amber-100 text-amber-800",
  in_transit: "bg-blue-100 text-blue-800",
  delivered:  "bg-green-100 text-green-800",
  draft:      "bg-slate-100 text-slate-500",
  created:    "bg-slate-100 text-slate-900",
  dispatched: "bg-amber-100 text-amber-800",
  cancelled:  "bg-red-100 text-red-800",
};

const STATUS_LABELS: Record<string, string> = {
  pending:    "Pending",
  in_transit: "In transit",
  delivered:  "Delivered",
  draft:      "Draft",
  created:    "Created",
  dispatched: "Dispatched",
  cancelled:  "Cancelled",
};

const StatusBadge: React.FC<{ status: string }> = ({ status }) => (
  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_STYLES[status] || "bg-slate-100 text-slate-900"}`}>
    {STATUS_LABELS[status] || status}
  </span>
);

const DashboardStatCard: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
  <div className="rounded-xl bg-slate-50 p-4 flex flex-col justify-center items-center">
    <span className="text-[26px] font-semibold mb-1" style={{ color }}>{value}</span>
    <span className="text-xs font-medium text-slate-500">{label}</span>
  </div>
);

const InvoiceActions: React.FC<{ consignment: APIConsignment }> = ({ consignment }) => {
  const link = consignment.link;
  const invoiceLabel = getInvoiceNo(consignment) || consignment.EWAYBILLNO || "";

  // Turn relative API PDF URL into absolute if needed
  const getDownloadUrl = () => {
    if (!link) return "";
    if (link.startsWith("http")) return link;
    if (API_BASE && link.startsWith("/")) return `${API_BASE}${link}`;
    return link;
  };

  const downloadUrl = getDownloadUrl();

  const handleView = () => {
    if (downloadUrl) window.open(downloadUrl, "_blank");
  };
  const handleDownload = () => {
    if (downloadUrl) {
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = invoiceLabel ? `Invoice_${invoiceLabel}` : "Invoice";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  };
  const handleShare = () => {
    if (navigator.share && downloadUrl) {
      navigator.share({ title: `Invoice ${invoiceLabel}`, url: downloadUrl });
    } else if (downloadUrl) {
      navigator.clipboard.writeText(downloadUrl);
      alert("Invoice link copied!");
    }
  };
  if (!link) {
    return <span className="text-slate-400">No invoice</span>;
  }
  return (
    <div className="flex items-center gap-2">
      <button className="hover:bg-slate-100 rounded p-1" title="View Invoice" onClick={handleView}>
        <FiEye className="w-4 h-4 text-blue-700" />
      </button>
      <button className="hover:bg-slate-100 rounded p-1" title="Download Invoice" onClick={handleDownload}>
        <FiDownload className="w-4 h-4 text-green-700" />
      </button>
      <button className="hover:bg-slate-100 rounded p-1" title="Share Invoice" onClick={handleShare}>
        <FiShare2 className="w-4 h-4 text-purple-600" />
      </button>
    </div>
  );
};

const RowActions: React.FC<{
  consignment: APIConsignment;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
}> = ({ consignment, onEdit, onDelete }) => (
  <div className="flex items-center gap-2">
    <button className="hover:bg-slate-100 rounded p-1" title="Edit" onClick={() => onEdit(consignment.id)}>
      <FiEdit className="w-4 h-4 text-amber-600" />
    </button>
    <button className="hover:bg-slate-100 rounded p-1" title="Delete" onClick={() => onDelete(consignment.id)}>
      <FiTrash className="w-4 h-4 text-red-700" />
    </button>
  </div>
);

const ConsignorDashboard: React.FC = () => {
  const [consignments, setConsignments] = useState<APIConsignment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeFilter, setActiveFilter] = useState<FilterOption>("all");
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/invoiceapi/invoice`)
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setConsignments(
            data.map((c: any) => {
              let invoiceNo = c["InvoiceNo"] ?? c["InvoiceNo."] ?? "";
              return {
                id: c.id,
                sender: c.sender,
                company: c.company,
                receiver: c.receiver,
                transporter: c.transporter,
                driver: c.driver,
                vehicles: c.vehicles,
                payment: c.payment,
                source: c.source,
                destination: c.destination,
                receiverAmountStatatus: c.receiverAmountStatatus,
                senderAmountStatatus: c.senderAmountStatatus,
                transporterAmountStatatus: c.transporterAmountStatatus,
                paymentStatus: c.paymentStatus,
                paymentPayableConsignor: c.paymentPayableConsignor,
                ItemsDescription: c.ItemsDescription,
                InvoiceNo: invoiceNo,
                "InvoiceNo.": c["InvoiceNo."],
                EWAYBILLNO: c.EWAYBILLNO,
                subjects: c.subjects,
                link: c.link ?? c.invoiceLink,
                status: c.status ?? undefined,
              };
            })
          );
        } else {
          setConsignments([]);
        }
      })
      .catch(() => setError("Unable to fetch consignments"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = activeFilter === "all"
    ? consignments
    : consignments.filter((c) => getConsignmentStatus(c) === activeFilter);

  const handleEdit = (id: number) => navigate(`/consignor/consignments/edit/${id}`);

  const handleDelete = (id: number) => {
    if (
      !window.confirm("Are you sure you want to delete this consignment? This action cannot be undone.")
    ) return;
    setConsignments(prev => prev.filter(c => c.id !== id));
  };

  const stats = [
    {
      label: "Total",
      value: consignments.length,
      color: "#185FA5"
    },
    {
      label: "Pending",
      value: consignments.filter((c) => getConsignmentStatus(c) === "pending").length,
      color: "#854F0B"
    },
    {
      label: "In transit",
      value: consignments.filter((c) => getConsignmentStatus(c) === "in_transit").length,
      color: "#534AB7"
    },
    {
      label: "Delivered",
      value: consignments.filter((c) => getConsignmentStatus(c) === "delivered").length,
      color: "#3B6D11"
    }
  ];

  return (
    <div className="p-4 md:p-6 space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {stats.map(stat => (
          <DashboardStatCard key={stat.label} {...stat} />
        ))}
      </div>
      {/* Panel/Table */}
      <div className="bg-white border border-slate-100 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-[16px] font-medium text-slate-900">My Consignments</h2>
            <p className="text-xs text-slate-400">View and manage your consignments & invoices</p>
          </div>
          <button
            className="text-xs px-3 py-1 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 transition-colors"
            onClick={() => navigate("/consignor/consignments/create")}
          >
            + New Consignment
          </button>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-4">
          {FILTER_OPTIONS.map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setActiveFilter(key)}
              className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                activeFilter === key
                  ? "bg-slate-900 text-white border-transparent"
                  : "border-slate-200 text-slate-500 hover:bg-slate-50"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[950px] text-[13px]">
            <thead>
              <tr className="text-xs border-b border-slate-100">
                <th className="py-2 px-3 font-semibold text-slate-500">Invoice No.</th>
                <th className="py-2 px-3 font-semibold text-slate-500">From</th>
                <th className="py-2 px-3 font-semibold text-slate-500">To</th>
                <th className="py-2 px-3 font-semibold text-slate-500">Subjects</th>
                <th className="py-2 px-3 font-semibold text-slate-500">Amount</th>
                <th className="py-2 px-3 font-semibold text-slate-500">Payment Mode</th>
                <th className="py-2 px-3 font-semibold text-slate-500">Status</th>
                <th className="py-2 px-3 font-semibold text-slate-500">Invoice</th>
                <th className="py-2 px-3 font-semibold text-slate-500"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="py-6 text-center">Loading...</td></tr>
              ) : error ? (
                <tr><td colSpan={9} className="py-6 text-center text-red-600">{error}</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="py-8 text-center text-slate-400">No consignments found.</td></tr>
              ) : (
                filtered.map((c) => (
                  <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50 transition-all">
                    <td className="py-2 px-3 font-medium text-slate-900">{getInvoiceNo(c)}</td>
                    <td className="py-2 px-3 text-slate-700">{c.source || "-"}</td>
                    <td className="py-2 px-3 text-slate-700">{c.destination || "-"}</td>
                    <td className="py-2 px-3 text-slate-700">{c.subjects || "-"}</td>
                    <td className="py-2 px-3 text-slate-700">{c.payment || "-"}</td>
                    <td className="py-2 px-3 text-slate-700 capitalize">
                      {c.paymentStatus === "to_pay"
                        ? "To pay"
                        : c.paymentStatus?.charAt(0).toUpperCase() + c.paymentStatus?.slice(1)}
                    </td>
                    <td className="py-2 px-3"><StatusBadge status={getConsignmentStatus(c)} /></td>
                    <td className="py-2 px-3"><InvoiceActions consignment={c} /></td>
                    <td className="py-2 px-3 text-right">
                      <RowActions
                        consignment={c}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ConsignorDashboard;